<template>
    <div class="flex flex-col w-full px-5 gap-3">
        <div class="flex items-center gap-2">
            <app-filter class="flex-grow" ref="filter" v-model="filterAsString" @input="updateState"></app-filter>
            <div class="flex gap-2 justify-center items-end">
                <div class="flex gap-2 justify-center my-2">
                    <app-button
                        size="mini"
                        icon="icon-chevron-left"
                        :title="$t('project.follow.byBundle.previousWeeks')"
                        @click="previousRange()"
                    ></app-button>
                    <div class="text-sm md:flex-col gap-2 md:gap-0 flex flex-row">
                        <div class="flex items-center">
                            <app-date-link
                                :label="$t('commons.from')"
                                v-model="startDate"
                                @input="updateState"
                            ></app-date-link>
                        </div>
                        <div class="flex items-center">
                            <app-date-link
                                :label="$t('commons.to')"
                                v-model="endDate"
                                @input="updateState"
                            ></app-date-link>
                        </div>
                    </div>
                    <app-button
                        size="mini"
                        icon="icon-chevron-right"
                        :title="$t('project.follow.byBundle.nextWeeks')"
                        @click="nextRange()"
                    ></app-button>
                </div>
            </div>
        </div>
        <div class="flex justify-start w-full">
            <app-button
                size="mini"
                icon="icon-file-download-outline"
                @click="downloadCSV()"
                :title="$t('organizations.downloadCSV')"
            />
        </div>
        <table class="text-xs border">
            <thead>
                <th class="p-1 border text-left" style="width: 5em">
                    <button
                        v-if="sortBy === 'billingDate'"
                        class="hover:underline flex justify-center w-full"
                        @click="sortAsc = !sortAsc"
                    >
                        {{ $t('commons.date') }}
                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                        <icon-menu-down v-else width="16" height="16" />
                    </button>
                    <button v-else class="hover:underline flex justify-center w-full" @click="sortBy = 'billingDate'">
                        {{ $t('commons.date') }}
                    </button>
                </th>
                <th class="p-1 border text-left" style="width: 6em">
                    <button
                        v-if="sortBy === 'invoiceNumber'"
                        class="hover:underline flex justify-center w-full"
                        @click="sortAsc = !sortAsc"
                    >
                        {{ $t('organizations.billings.invoiceNumber') }}
                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                        <icon-menu-down v-else width="16" height="16" />
                    </button>
                    <button v-else class="hover:underline flex justify-center w-full" @click="sortBy = 'invoiceNumber'">
                        {{ $t('organizations.billings.invoiceNumber') }}
                    </button>
                </th>
                <th class="p-1 border text-left" style="width: 10em">
                    <button
                        v-if="sortBy === 'code'"
                        class="hover:underline flex justify-center w-full"
                        @click="sortAsc = !sortAsc"
                    >
                        {{ $t('commons.code') }}
                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                        <icon-menu-down v-else width="16" height="16" />
                    </button>
                    <button v-else class="hover:underline flex justify-center w-full" @click="sortBy = 'code'">
                        {{ $t('commons.code') }}
                    </button>
                </th>
                <th class="p-1 border text-left" style="">
                    <button
                        v-if="sortBy === 'name'"
                        class="hover:underline flex justify-center w-full"
                        @click="sortAsc = !sortAsc"
                    >
                        {{ $t('commons.name') }}
                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                        <icon-menu-down v-else width="16" height="16" />
                    </button>
                    <button v-else class="hover:underline flex justify-center w-full" @click="sortBy = 'name'">
                        {{ $t('commons.name') }}
                    </button>
                </th>
                <th class="p-1 border text-left" style="">
                    <button
                        v-if="sortBy === 'city'"
                        class="hover:underline flex justify-center w-full"
                        @click="sortAsc = !sortAsc"
                    >
                        {{ $t('commons.city') }}
                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                        <icon-menu-down v-else width="16" height="16" />
                    </button>
                    <button v-else class="hover:underline flex justify-center w-full" @click="sortBy = 'city'">
                        {{ $t('commons.city') }}
                    </button>
                </th>

                <th class="p-1 border text-right" style="width: 10em">{{ $t('organizations.billings.amountHT') }}</th>
                <th class="p-1 border text-right" style="width: 10em">{{ $t('organizations.billings.amountTTC') }}</th>
                <th class="p-1 border" style="width: 4em">
                    <div class="flex justify-center">
                        <icon-rotate-right v-if="prepareZip" class="animate animate-spin"></icon-rotate-right>
                        <app-button
                            variant="borderless"
                            size="mini"
                            v-else
                            icon="icon-download"
                            class="text-xs"
                            @click="downloadAll"
                            :title="$t('organizations.downloadPDF')"
                        ></app-button>
                    </div>
                </th>
            </thead>

            <tr v-for="billing in filteredBillings">
                <td class="p-1 border">{{ billing.billingDate | humanizeDate }}</td>
                <td class="p-1 border">
                    {{ billing.invoiceNumber }}
                </td>
                <td class="p-1 border">
                    {{ billing.code }}
                </td>
                <td class="p-1 border">
                    {{ billing.name }}
                </td>
                <td class="p-1 border">
                    {{ billing.city }}
                </td>
                <td class="p-1 border text-right">
                    {{ $n(billing.totalHT, currency) }}
                </td>
                <td class="p-1 border text-right">
                    {{ $n(billing.totalHT + billing.totalHT * 0.2, currency) }}
                </td>
                <td class="p-1 border">
                    <div class="flex justify-center">
                        <app-file-link
                            class="text-xs"
                            v-if="billing.billUrl"
                            :file-name="billing.fileName"
                            :url="billing.billUrl"
                            :show-name="false"
                            :title="$t('organizations.downloadFile') + ' ' + billing.fileName"
                        ></app-file-link>
                    </div>
                </td>
            </tr>
        </table>
    </div>
</template>

<script>
import AppInputText from '@/components/appInputText/AppInputText.vue';
import { getOrganization, getOrganizationBillings } from '@/features/organizations/organizations.service';
import AppPhone from '@/components/appPhone/AppPhone.vue';
import { getLoggedUser } from '@/features/tokens/token.service';
import AppPhoneInput from '@/components/appPhoneInput/AppPhoneInput.vue';
import AppMail from '@/components/appMail/AppMail.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
import AppFooter from '@/components/appFooter/AppFooter.vue';
import { updateBreadCrumbs } from '@/state/state';
import IconFileDownloadOutline from '@/icons/IconFileDownloadOutline.vue';
import AppFileLink from '@/components/appFileLink/AppFileLink.vue';
import AppFilter from '@/components/appFilter/AppFilter.vue';
import { filterMatch } from '@/services/sanitize.service';
import { humanizeDate, humanizeDateShort } from '@/filters/dateFilter';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import { startOfDay, addYears, endOfDay, subYears } from 'date-fns';
import { sortBy } from '@/services/sanitize.service';
import isValid from 'date-fns/isValid';
import endOfYear from 'date-fns/endOfYear';
import startOfYear from 'date-fns/startOfYear';
import { getAPIHeaders } from '@/services/api.service';

export default {
    components: {
        AppDateLink,
        AppDateInput,
        AppFilter,
        AppFileLink,
        IconFileDownloadOutline,
        AppFooter,
        AppPhoneInput,
        AppInputText,
        AppPopup,
        AppMail,
        AppPhone,
        AppButton,
    },
    created() {
        this.init();
    },
    watch: {
        $route() {
            this.init();
        },
    },
    computed: {
        minDate() {
            return startOfDay(this.startDate);
        },
        maxDate() {
            return endOfDay(this.endDate);
        },
        filteredBillings() {
            const list = sortBy(
                this.billings.filter(
                    (item) =>
                        this.datesMatch(item.billingDate, this.minDate, this.maxDate) &&
                        filterMatch(item.filterString, this.filterAsString, true),
                ),
                this.sortBy,
            );
            if (!this.sortAsc) {
                list.reverse();
            }
            return list;
        },
    },
    methods: {
        async downloadAll() {
            this.prepareZip = true;
            const response = await fetch(
                new Request(`/api/organizations/${this.$route.params.organizationId}/billingsAsZip`, {
                    method: 'POST',
                    headers: getAPIHeaders({ 'content-type': `application/json` }),
                    body: JSON.stringify(this.filteredBillings.map((item) => item.id)),
                }),
            );
            this.downloadAsZipFile(response);
            this.prepareZip = false;
        },
        downloadCSV: function () {
            const header = 'Date,Numéro,Code Opération,Nom Opération,Ville Opération,Montant HC,Montant TTC,Lien PDF\n';
            this.downloadAsCSVFile(
                header +
                    this.filteredBillings
                        .map((billing) =>
                            [
                                humanizeDate(billing.billingDate) || '',
                                billing.invoiceNumber || '',
                                billing.code || '',
                                billing.name || '',
                                billing.city || '',
                                this.$n(billing.totalHT, this.currency),
                                this.$n(billing.totalHT + billing.totalHT * 0.2, this.currency),
                                billing.billUrl,
                            ]
                                .map((entry) => '"' + entry.replaceAll('"', '') + '"')
                                .join(),
                        )
                        .join('\n'),
            );
        },
        datesMatch: function (date, min, max) {
            return (!isValid(min) || min.getTime() < date.getTime()) && (!isValid(max) || date.getTime() < max);
        },
        nextRange() {
            if (isValid(this.startDate)) {
                this.startDate = addYears(this.minDate, 1);
            }
            if (isValid(this.endDate)) {
                this.endDate = addYears(this.maxDate, 1);
            }
            this.updateState();
        },
        previousRange() {
            if (isValid(this.startDate)) {
                this.startDate = subYears(this.minDate, 1);
            }
            if (isValid(this.endDate)) {
                this.endDate = subYears(this.maxDate, 1);
            }
            this.updateState();
        },
        updateState() {
            localStorage.setItem(
                'organizations.filters',
                JSON.stringify({
                    filterAsString: this.filterAsString,
                    startDate: isValid(this.startDate) ? startOfDay(this.startDate) : null,
                    endDate: isValid(this.endDate) ? endOfDay(this.endDate) : null,
                }),
            );
        },
        async init() {
            this.organization = await getOrganization(this.$route.params.organizationId);
            updateBreadCrumbs({ organizationName: this.organization.name });

            this.billings = (await getOrganizationBillings(this.$route.params.organizationId)).map((item) => ({
                ...item,
                fileName: this.$t('order.project.orders.bill') + '_' + item.invoiceNumber + '.pdf',
                filterString:
                    humanizeDate(item.billingDate) +
                    '_' +
                    item.name +
                    '_' +
                    item.code +
                    '_' +
                    item.invoiceNumber +
                    '_' +
                    item.city +
                    '_' +
                    this.$n(item.totalHT, this.currency) +
                    '_' +
                    this.$n(item.totalHT + item.totalHT * 0.2, this.currency),
            }));

            const state = localStorage.getItem('organizations.filters')
                ? JSON.parse(localStorage.getItem('organizations.filters'))
                : null;
            if (state) {
                this.filterAsString = state.filterAsString;
                this.startDate = new Date(state.startDate || startOfYear(new Date()));
                this.endDate = new Date(state.endDate || endOfYear(new Date()));
            } else {
                this.filterAsString = '';
                if (this.billings.length > 0) {
                    this.startDate = new Date(
                        this.billings.reduce(
                            (acc, { billingDate }) =>
                                acc && acc < billingDate.getTime() ? acc : billingDate.getTime(),
                            0,
                        ),
                    );
                    this.endDate = new Date(
                        this.billings.reduce(
                            (acc, { billingDate }) =>
                                acc && acc > billingDate.getTime() ? acc : billingDate.getTime(),
                            0,
                        ),
                    );
                } else {
                    this.startDate = startOfYear(new Date());
                    this.endDate = endOfYear(new Date());
                }
                this.endDate = new Date(
                    this.billings.reduce(
                        (acc, { billingDate }) => (acc && acc > billingDate.getTime() ? acc : billingDate.getTime()),
                        0,
                    ),
                );
            }

            const token = getLoggedUser();
            const isRoot = token && token.roles.includes('root');
            const isOwner = this.organization.ownerId === token.id;
            this.readOnly = !isRoot && !isOwner;
        },
        async downloadAsZipFile(response) {
            const a = document.createElement('a');
            const fileContents = await response.blob();
            const url = URL.createObjectURL(fileContents);
            a.setAttribute('href', url);
            a.download = `facturesTex_${this.startDate ? humanizeDateShort(this.startDate) : ''}_${
                this.endDate ? humanizeDateShort(this.endDate) : ''
            }.zip`;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(function () {
                URL.revokeObjectURL(a.href);
            }, 1500);
        },
        async downloadAsCSVFile(content) {
            const a = document.createElement('a');
            a.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(content));
            a.download = `facturesTex_${this.startDate ? humanizeDateShort(this.startDate) : ''}_${
                this.endDate ? humanizeDateShort(this.endDate) : ''
            }.csv`;
            a.dataset.downloadurl = ['text/csv', a.download, a.href].join(':');
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(function () {
                URL.revokeObjectURL(a.href);
            }, 1500);
        },
    },
    data() {
        return {
            filterAsString: '',
            billings: [],
            readOnly: true,
            organization: { owner: {} },
            currency: {
                currency: 'EUR',
                style: 'currency',
            },
            startDate: null,
            endDate: null,
            sortBy: 'billingDate',
            sortAsc: true,
            prepareZip: false,
        };
    },
};
</script>
